import React, {
  useEffect,
  useRef,
  useMemo,
  useState,
  useCallback,
} from "react";
import { Flex, Box } from "rebass/styled-components";
import styled, { css } from "styled-components";
import { useSetRecoilState } from "recoil";
import { useHistory } from "react-router-dom";
import { HiChevronLeft, HiChevronRight, HiX } from "react-icons/hi";
import { MdDesktopWindows, MdPhoneAndroid, MdMovie } from "react-icons/md";
import { Body } from "MVVM/Components/Text";
import { Subheader } from "MVVM/Components/Text";
import { GameLogos } from "MVVM/Components/Icons";
import { useUser } from "MVVM/Hooks/useUser";
import { analytics } from "MVVM/Analytics";
import { etaFromIso } from "MVVM/Utilities/approxTimeDisplay";
import { ESupportedGames, ModalType } from "MVVM/@types";
import { ClipFullModalState } from "MVVM/State/modals";
import type { ClipStatusTimeline } from "../../@types";
import { OverallState, MontageProgressState } from "../../@types";
import {
  MAX_STEP_IDX,
  buildSupportLink,
  getCapturingFill,
  getSegmentDuration,
  getStatusCopy,
  isTimelineStale,
  resolveActiveIndex,
} from "../../timelineDisplay";
import { useMontageProgress } from "../../Hooks/useMontageProgress";
import type { TVodTrayMeta } from "../../Hooks/useVodTrayMeta";
import { matchVodTrayUrl, stripVodSuffix } from "../../Hooks/useVodTrayMeta";
import { Stepper } from "./Stepper";
import { SkipTheWaitCTA } from "./SkipTheWaitCTA";
import { KeepTheSpeedCTA } from "./KeepTheSpeedCTA";

type TrackerCardProps = {
  timelines: ClipStatusTimeline[];
  montageId?: string;
  vodMeta?: TVodTrayMeta;
  onDismiss?: () => void;
};

const getClipProgress = (timeline: ClipStatusTimeline): number => {
  const idx = resolveActiveIndex(timeline.events);
  if (
    timeline.overallState === OverallState.COMPLETED ||
    timeline.overallState === OverallState.COMPLETED_DESKTOP
  ) {
    return 1;
  }
  if (idx < 0) return 0;
  return idx / MAX_STEP_IDX;
};

export const TrackerCard = ({
  timelines,
  montageId,
  vodMeta,
  onDismiss,
}: TrackerCardProps) => {
  const { allstarUser } = useUser();
  const history = useHistory();
  const setClipModal = useSetRecoilState(ClipFullModalState);
  const [pageIndex, setPageIndex] = useState(0);

  const { montage } = useMontageProgress(montageId);

  const isMontage = !!montageId;
  const clips = isMontage && montage?.clips?.length ? montage.clips : timelines;
  const isFullMatchVod = clips.some((c) => c.contentType === "FULL_MATCH_VOD");
  const montageNoun = isFullMatchVod
    ? stripVodSuffix(vodMeta?.title) || "Match VOD"
    : "Montage";
  const vodUrl = isFullMatchVod ? matchVodTrayUrl(vodMeta) : null;
  // Stable order across polls (firstEventAt, trackId tiebreak): montage "Part N"
  // reads chronologically, pagination tabs show newest first.
  const chronologicalClips = useMemo(
    () =>
      [...clips].sort((a, b) => {
        const byFirstEvent = (a.firstEventAt ?? "").localeCompare(
          b.firstEventAt ?? "",
        );
        return byFirstEvent !== 0
          ? byFirstEvent
          : a.trackId.localeCompare(b.trackId);
      }),
    [clips],
  );
  const orderedClips = useMemo(
    () => [...chronologicalClips].reverse(),
    [chronologicalClips],
  );
  const orderedMontageClips = chronologicalClips;
  const safeIndex = Math.max(0, Math.min(pageIndex, orderedClips.length - 1));
  const timeline = orderedClips[safeIndex];
  const showPagination = !isMontage && orderedClips.length > 1;

  const prev = useCallback(() => setPageIndex((i) => Math.max(0, i - 1)), []);
  const next = useCallback(
    () => setPageIndex((i) => Math.min(orderedClips.length - 1, i + 1)),
    [orderedClips.length],
  );

  // Focus a freshly created clip's tab when it first appears (newest sorts first).
  const knownTrackIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    const seen = knownTrackIds.current;
    const ids = orderedClips.map((c) => c.trackId);
    const hasNewClip = ids.some((id) => !seen.has(id));
    knownTrackIds.current = new Set(ids);
    if (hasNewClip && seen.size > 0 && !isMontage) {
      setPageIndex(0);
    }
  }, [orderedClips, isMontage]);

  const isFragmovie = timeline.contentType === "FRAGMOVIE";

  const activeIdx = resolveActiveIndex(timeline.events);
  const isError = timeline.overallState === OverallState.ERRORED;
  const isComplete =
    timeline.overallState === OverallState.COMPLETED ||
    timeline.overallState === OverallState.COMPLETED_DESKTOP;

  const fillDuration = getSegmentDuration(
    activeIdx,
    timeline.desktop?.estimatedCompletionAt,
  );
  const activeFill = getCapturingFill(
    activeIdx,
    timeline.events,
    timeline.desktop?.estimatedCompletionAt,
  );

  const shownRef = useRef(false);
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    analytics.track("Clip Status Card Shown", {
      trackId: timeline.trackId,
      game: timeline.game,
      contentType: timeline.contentType,
      overallState: timeline.overallState,
      queueName: timeline.queueName,
      montageId,
      clipCount: clips.length,
    });
  }, [timeline, montageId, clips.length]);

  const isStale = isTimelineStale(timeline);

  const eta = timeline.desktop?.estimatedCompletionAt
    ? etaFromIso(timeline.desktop.estimatedCompletionAt)
    : null;

  const clipTitleMap = useRef(new Map<string, string>());
  if (timeline.clip?.title)
    clipTitleMap.current.set(timeline.trackId, timeline.clip.title);
  const clipTitle = clipTitleMap.current.get(timeline.trackId);

  const statusCopy = getStatusCopy(timeline);

  const terminalVariant: "completed" | "errored" | "none" = isComplete
    ? "completed"
    : isError
    ? "errored"
    : "none";

  const isMontageTerminal =
    montage?.state === MontageProgressState.COMPLETED ||
    montage?.state === MontageProgressState.ERROR;
  const montageLabel = montageId
    ? montage && !isMontageTerminal
      ? `${montageNoun}: ${montage.completed} of ${montage.total} rendered`
      : montage?.state === MontageProgressState.ERROR
      ? `${montageNoun} error`
      : montageNoun
    : null;

  const isTerminal = isComplete || isError;
  const desktopReady = !!timeline.desktop?.completedAt;
  const mobileReady = !!timeline.mobile?.completedAt;
  const hasMobile = !!timeline.mobile;

  const clipPageHref = timeline.clipId ? `/clip/${timeline.clipId}` : null;

  const goToClipPage = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      // Let cmd/ctrl/middle click fall through to the browser's new tab.
      if (event.metaKey || event.ctrlKey || event.shiftKey) return;
      if (!clipPageHref) return;
      event.preventDefault();
      analytics.track("Clip Status CTA Clicked", {
        trackId: timeline.trackId,
        game: timeline.game,
        contentType: timeline.contentType,
        action: "open_clip_page",
      });
      history.push(clipPageHref);
    },
    [
      clipPageHref,
      history,
      timeline.trackId,
      timeline.game,
      timeline.contentType,
    ],
  );

  const openClip = useCallback(
    (orientation: "LANDSCAPE" | "PORTRAIT") => {
      if (!timeline.clipId) return;
      setClipModal({
        isOpen: ModalType.Clip,
        shareId: timeline.clipId,
        orientation,
      });
      analytics.track("Clip Status CTA Clicked", {
        trackId: timeline.trackId,
        game: timeline.game,
        contentType: timeline.contentType,
        action: "watch",
        orientation,
      });
    },
    [
      timeline.clipId,
      timeline.trackId,
      timeline.game,
      timeline.contentType,
      setClipModal,
    ],
  );

  const montageTerminalVariant: "completed" | "errored" | "none" = isMontage
    ? montage?.state === MontageProgressState.COMPLETED
      ? "completed"
      : montage?.state === MontageProgressState.ERROR
      ? "errored"
      : "none"
    : "none";

  const isMontageComplete = montage?.state === MontageProgressState.COMPLETED;

  if (isMontage) {
    return (
      <CardBox
        role="status"
        aria-live="polite"
        $terminal={montageTerminalVariant}
      >
        <TopRow>
          <MontageLabel>{montageLabel ?? montageNoun}</MontageLabel>
          {onDismiss && (
            <DismissButton onClick={onDismiss} aria-label="Dismiss">
              <HiX size={14} />
            </DismissButton>
          )}
        </TopRow>

        {isMontageComplete ? (
          <ReadyRows>
            <ReadyRow>
              <MdDesktopWindows size={14} />
              <ReadyText>
                Your {isFullMatchVod ? "match VOD" : "montage"} is ready
              </ReadyText>
              <ViewLink
                as="a"
                href={vodUrl ?? `/montage?montage=${montageId}`}
                onClick={() => {
                  analytics.track("Clip Status CTA Clicked", {
                    montageId,
                    action: "watch",
                    contentType: isFullMatchVod ? "FULL_MATCH_VOD" : "MONTAGE",
                  });
                  onDismiss?.();
                }}
              >
                View
              </ViewLink>
            </ReadyRow>
          </ReadyRows>
        ) : (
          <MontageClipList>
            {orderedMontageClips.map((clip, i) => {
              const progress = getClipProgress(clip);
              const clipDone =
                clip.overallState === OverallState.COMPLETED ||
                clip.overallState === OverallState.COMPLETED_DESKTOP;
              const clipErrored = clip.overallState === OverallState.ERRORED;

              return (
                <MontageClipRow key={clip.trackId}>
                  <MontageClipLabel $done={clipDone} $error={clipErrored}>
                    {isFullMatchVod ? "Part" : "Clip"} {i + 1}
                  </MontageClipLabel>
                  <MiniBarTrack>
                    <MiniBarFill
                      $progress={progress}
                      $done={clipDone}
                      $error={clipErrored}
                    />
                  </MiniBarTrack>
                </MontageClipRow>
              );
            })}
          </MontageClipList>
        )}
      </CardBox>
    );
  }

  return (
    <CardBox role="status" aria-live="polite" $terminal={terminalVariant}>
      <HeaderRow>
        <HeaderLeft>
          {timeline.game != null && (
            <GameLogos
              type={timeline.game as unknown as ESupportedGames}
              size={["16", "18"]}
              styles={{ marginRight: 0 }}
            />
          )}
          {isFragmovie && (
            <FragmovieIcon title="Fragmovie">
              <MdMovie size={14} />
            </FragmovieIcon>
          )}
        </HeaderLeft>
        <HeaderActions>
          {eta && !isError && !isComplete && (
            <EtaText color={isStale ? "caution" : "envy"}>{eta}</EtaText>
          )}
          {showPagination && (
            <PaginationNav>
              <NavButton onClick={prev} disabled={safeIndex === 0}>
                <HiChevronLeft size={14} />
              </NavButton>
              <PageIndicator>
                {safeIndex + 1}/{orderedClips.length}
              </PageIndicator>
              <NavButton
                onClick={next}
                disabled={safeIndex === orderedClips.length - 1}
              >
                <HiChevronRight size={14} />
              </NavButton>
            </PaginationNav>
          )}
          {onDismiss && (
            <DismissButton onClick={onDismiss} aria-label="Dismiss">
              <HiX size={14} />
            </DismissButton>
          )}
        </HeaderActions>
      </HeaderRow>

      <TitleRow>
        {clipPageHref ? (
          <ClipTitleLink href={clipPageHref} onClick={goToClipPage}>
            {clipTitle || "Clip"}
          </ClipTitleLink>
        ) : (
          <ClipTitle>{clipTitle || "New clip"}</ClipTitle>
        )}
      </TitleRow>

      <ContentArea>
        {isComplete ? (
          <ReadyRows>
            <ReadyRow>
              <MdDesktopWindows size={14} />
              <ReadyText>
                {desktopReady
                  ? "Your Desktop clip is ready"
                  : "Desktop rendering"}
              </ReadyText>
              {desktopReady && (
                <ViewLink onClick={() => openClip("LANDSCAPE")}>View</ViewLink>
              )}
            </ReadyRow>
            {hasMobile && (
              <ReadyRow>
                <MdPhoneAndroid size={14} />
                <ReadyText>
                  {mobileReady
                    ? "Your Mobile clip is ready"
                    : "Mobile rendering"}
                </ReadyText>
                {mobileReady && (
                  <ViewLink onClick={() => openClip("PORTRAIT")}>View</ViewLink>
                )}
              </ReadyRow>
            )}
          </ReadyRows>
        ) : (
          <>
            <Stepper
              key={timeline.trackId}
              activeIdx={activeIdx}
              isError={isError}
              fillDuration={fillDuration}
              activeFill={activeFill}
            />

            <StatusRow>
              <StatusText
                color={isError ? "alert" : isStale ? "caution" : "chalk"}
              >
                {statusCopy}
              </StatusText>
            </StatusRow>

            {(isError || isStale) && (
              <CTARow>
                <SupportLink
                  href={buildSupportLink(timeline, allstarUser?.user?._id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    analytics.track("Clip Status CTA Clicked", {
                      trackId: timeline.trackId,
                      game: timeline.game,
                      contentType: timeline.contentType,
                      action: "get_help",
                    })
                  }
                >
                  Get help
                </SupportLink>
              </CTARow>
            )}

            <SkipTheWaitCTA
              timeline={timeline}
              activeIdx={activeIdx}
              isStale={isStale}
              isError={isError}
            />
            <KeepTheSpeedCTA
              timeline={timeline}
              activeIdx={activeIdx}
              isStale={isStale}
              isError={isError}
            />
          </>
        )}
      </ContentArea>
    </CardBox>
  );
};

const CardBox = styled(Box)<{
  $terminal: "completed" | "errored" | "none";
}>`
  background-color: ${({ $terminal, theme }) =>
    $terminal === "none" ? theme.colors.navy : theme.colors.navy};
  border-radius: 12px;
  border: 1px solid
    ${({ $terminal, theme }) =>
      $terminal === "completed"
        ? theme.colors.envyAlpha20
        : $terminal === "errored"
        ? theme.colors.alertAlpha20
        : theme.colors.chalkAlpha20};
  padding: 10px 12px;
  width: 300px;

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    width: 100%;
  }
`;

const TopRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const MontageLabel = styled(Body)`
  font-weight: 600;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.ice};
`;

const HeaderActions = styled(Flex)`
  align-items: center;
  gap: 6px;
  margin-left: auto;
  flex-shrink: 0;
`;

const PaginationNav = styled(Flex)`
  align-items: center;
  gap: 2px;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  padding: 0;
  cursor: pointer;
  opacity: 0.7;

  &:hover:not(:disabled) {
    opacity: 1;
  }

  &:disabled {
    opacity: 0.2;
    cursor: default;
  }
`;

const PageIndicator = styled(Body)`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  min-width: 28px;
  text-align: center;
`;

const HeaderRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2px;
`;

// Separate left group so the ETA toggling can't shift the right-pinned arrows.
const HeaderLeft = styled(Flex)`
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const TitleRow = styled(Box)`
  margin-bottom: 6px;
`;

const ClipTitle = styled(Body)`
  display: block;
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ClipTitleLink = styled(ClipTitle).attrs({ as: "a" })`
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const FragmovieIcon = styled(Box).attrs({ as: "span" })`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: default;
  display: inline-flex;
  flex-shrink: 0;
`;

const EtaText = styled(Body)`
  font-weight: 600;
  font-size: 12px;
  flex-shrink: 0;
`;

const StatusRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
`;

const StatusText = styled(Subheader)`
  font-weight: 500;
  font-size: 12px;
`;

// Reserve a consistent height so in-progress and completed tabs don't jump.
const ContentArea = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 44px;
`;

const ReadyRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`;

const ReadyRow = styled(Flex)`
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.envy};
`;

const ReadyText = styled(Subheader)`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.chalk};
`;

const ViewLink = styled.button`
  margin-left: auto;
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const CTARow = styled(Flex)`
  gap: 8px;
  margin-top: 6px;

  &:empty {
    display: none;
  }
`;

const SupportLink = styled.a`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.ice};
  text-decoration: underline;
  text-decoration-color: ${({ theme }) => theme.colors.envy};
  text-underline-offset: 2px;
  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const DismissButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: none;
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.chalk};
    background-color: ${({ theme }) => theme.colors.chalkAlpha20};
  }
`;

const MontageClipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 2px;
`;

const MontageClipRow = styled(Flex)`
  align-items: center;
  gap: 8px;
`;

const MontageClipLabel = styled(Subheader)<{
  $done: boolean;
  $error: boolean;
}>`
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
  width: 38px;
  color: ${({ $done, $error, theme }) =>
    $error
      ? theme.colors.alert
      : $done
      ? theme.colors.envy
      : theme.colors.chalkAlpha60};
`;

const MiniBarTrack = styled.div`
  flex: 1;
  height: 3px;
  background-color: ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 2px;
  overflow: hidden;
`;

const MiniBarFill = styled.div<{
  $progress: number;
  $done: boolean;
  $error: boolean;
}>`
  height: 100%;
  width: ${({ $progress }) => `${$progress * 100}%`};
  border-radius: 2px;
  background-color: ${({ $done, $error, theme }) =>
    $error
      ? theme.colors.alert
      : $done
      ? theme.colors.envy
      : theme.colors.envy};
  transition: width 0.6s ease-out;
  ${({ $done, $error }) =>
    !$done &&
    !$error &&
    css`
      opacity: 0.7;
    `}
`;
